import { NetworkStatus, useQuery } from '@apollo/client'

import { gql } from '@/clients/utoniq-core'
import {
  CollectionDetailForGuestQuery,
  CollectionDetailForGuestQueryVariables,
  CollectionDetailQuery,
  CollectionDetailQueryVariables,
  CollectionItemsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useAuth, useMessage, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { Share } from '@/core/native'
import { spreadCollectionItemsBySku } from '@/core/sku'
import { useCreateShortLink } from '@/lib/url/shortLink'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const { user: currentUser } = useAuth()
  const { router } = useRouter<{ collectionId: string; userId?: string }>()
  const { data, refetch, fetchMore, networkStatus } = useQuery<
    CollectionDetailQuery,
    CollectionDetailQueryVariables
  >(QUERY, {
    variables: {
      userId: router.query.userId,
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      pageInput: {
        limit: 12,
      },
    },
    skip: !currentUser?.isSigned,
    returnPartialData: true,
  })

  const {
    data: dataForGuest,
    refetch: refetchForGuest,
    fetchMore: fetchMoreForGuest,
    networkStatus: networkStatusForGuest,
  } = useQuery<
    CollectionDetailForGuestQuery,
    CollectionDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      pageInput: {
        limit: 12,
      },
    },
    skip: !!currentUser?.isSigned,
    returnPartialData: true,
  })

  const isNextFetch = !!(data ?? dataForGuest)?.collection

  const getShortLinkUrl = useCreateShortLink({
    /**
     * ログインしている場合、ログインユーザーのUserCollectionDetailページのリンクを生成する
     * 未ログインの場合、collectionDetailページのリンクを生成する
     */
    path: currentUser
      ? `/userProfile/${router.query.userId ?? currentUser?.uid}/collections/${
          data?.collection?.id
        }`
      : `/collections/${data?.collection?.id}`,
  })

  return {
    state: {
      loading:
        (networkStatus ?? networkStatusForGuest) === NetworkStatus.loading &&
        !isNextFetch,
      fetching:
        (networkStatus ?? networkStatusForGuest) === NetworkStatus.fetchMore,
      user: data?.user ?? null,
      userId: currentUser?.uid === data?.user?.id ? undefined : data?.user?.id,
      hasNextPage: !!(data ?? dataForGuest)?.collection?.collectionItems
        ?.pageInfo?.hasNextPage,
      collection: (data ?? dataForGuest)?.collection ?? null,
      collectionItems: spreadCollectionItemsBySku(
        (data ?? dataForGuest)?.collection?.collectionItems
      ),
    },
    actions: {
      async onChangeLabel(isFirstLabel, name) {
        if (!currentUser) {
          await refetchForGuest({
            collectionId: router.query.collectionId,
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
          })
          return
        }

        await refetch({
          collectionId: router.query.collectionId,
          collectionItemsLabel: isFirstLabel ? undefined : name,
          collectionItemsFirstLabel: isFirstLabel,
        })
      },
      onClickShare: async () => {
        analytics?.track('click_share', {
          applicationKey: config.applicationKey,
          artistId: data?.collection?.artistId,
          collectionId: router.query.collectionId,
          name: 'collectionDetail',
        })
        const url = await getShortLinkUrl()
        await Share.share({
          title: data?.collection?.title,
          text: data?.collection?.description || '',
          url,
          dialogTitle: data?.collection?.title,
        })
          .then(() => {
            analytics?.track('click_shareMethod', {
              applicationKey: config.applicationKey,
              artistId: data?.collection?.artistId,
              collectionId: router.query.collectionId,
              name: 'collectionDetail',
            })
          })
          .catch(e => showError(e))

        return
      },
      async onFetchMore(isFirstLabel, name) {
        if (!currentUser) {
          await fetchMoreForGuest({
            variables: {
              collectionId: router.query.collectionId,
              collectionItemsLabel: isFirstLabel ? undefined : name,
              collectionItemsFirstLabel: isFirstLabel,
              pageInput: {
                limit: 12,
                cursor:
                  dataForGuest?.collection?.collectionItems?.pageInfo.endCursor,
              },
            },
          })
          return
        }

        await fetchMore({
          variables: {
            collectionId: router.query.collectionId,
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
            pageInput: {
              limit: 12,
              cursor: data?.collection?.collectionItems?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query CollectionDetail(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
    $userId: ID
  ) {
    user(id: $userId) {
      id
      displayName
      avatar {
        uri
        smallUri
      }
    }
    collection(id: $collectionId) {
      id
      title
      artistId
      artist {
        id
        displayName
      }
      thumbnail {
        uri
        mediumUri
      }
      userCollection(userId: $userId) {
        ogp {
          uri
          smallUri
        }
        itemOwnedCount
        totalOwnedCount
        totalOwnedItemCount @client
        ownedItems {
          itemId
          ownedCount
          userItem {
            serials {
              serial {
                skuId
              }
            }
          }
        }
        id
        userId
      }
      itemCount
      description
      labels {
        name
        id
      }
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        pageInput: $pageInput
      ) {
        ...CollectionItemsConnection
      }
    }
  }
  ${CollectionItemsConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query CollectionDetailForGuest(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
  ) {
    collection(id: $collectionId) {
      id
      title
      artistId
      artist {
        id
        displayName
      }
      thumbnail {
        uri
        mediumUri
      }
      itemCount
      description
      labels {
        name
        id
      }
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        pageInput: $pageInput
      ) {
        ...CollectionItemsConnection
      }
    }
  }
  ${CollectionItemsConnectionFragmentDoc}
`
